import { useContext, useMemo } from 'react';

import { getSeasonConfig } from './get-season-config';
import { SeasonalThemeContext } from './seasonal-theme-context';

export interface SeasonalAvatarOverlayConfig {
	/** Whether the seasonal avatar overlay is enabled */
	isEnabled: boolean;
	/** The overlay image URL (if enabled) */
	overlayImage: string | undefined;
	/** Scale multiplier for the overlay (default 1.4 = 140% of avatar size) */
	scale: number;
	/** Optional CSS translateY on the overlay (e.g. `6px`) for per-season alignment */
	translateY?: string;
}

/**
 * Hook to get seasonal avatar overlay configuration
 *
 * Returns the overlay image URL when seasonal theme is enabled and
 * the current season has an avatar overlay configured.
 *
 * Gracefully handles usage outside of SeasonalThemeProvider by returning
 * disabled state.
 *
 * @example
 * const { isEnabled, overlayImage, scale, translateY } = useSeasonalAvatarOverlay();
 *
 * if (isEnabled && overlayImage) {
 *   return <img src={overlayImage} style={{ width: `${scale * 100}%` }} />;
 * }
 */
export const useSeasonalAvatarOverlay = (): SeasonalAvatarOverlayConfig => {
	const context = useContext(SeasonalThemeContext);
	const seasonConfig = getSeasonConfig();

	// Gracefully handle usage outside of provider
	const isSeasonalThemeEnabled = context?.isSeasonalThemeEnabled ?? false;

	return useMemo(
		() => ({
			isEnabled: isSeasonalThemeEnabled && Boolean(seasonConfig.avatarOverlayImage),
			overlayImage: isSeasonalThemeEnabled ? seasonConfig.avatarOverlayImage : undefined,
			scale: seasonConfig.avatarOverlayScale ?? 1.4,
			translateY: seasonConfig.avatarOverlayTranslateY,
		}),
		[isSeasonalThemeEnabled, seasonConfig.avatarOverlayImage, seasonConfig.avatarOverlayScale, seasonConfig.avatarOverlayTranslateY]
	);
};
